/**
 * ProBadge — LinkedIn-Premium-style gold pill labelling tier-gated
 * features so demo presenters can differentiate Starter vs Pro vs
 * Enterprise scope without leaving the screen.
 *
 * Variants:
 *   <ProBadge />            → standard "PRO" gold pill
 *   <ProBadge tier="enterprise" />  → "ENTERPRISE" violet pill
 *   <ProBadge tier="beta" />       → "BETA" teal pill (preview features)
 *   <ProBadge size="sm" />          → smaller inline variant
 *
 * Sits inline next to a feature label, OR absolute-positioned over a
 * locked card.
 */
import React from 'react';
import { Sparkles, Lock, Zap } from 'lucide-react';

const PALETTES = {
  pro: {
    bg: 'linear-gradient(135deg, #F4C24E 0%, #FFA72A 100%)',
    color: '#1A1208',
    border: 'rgba(255, 184, 50, 0.55)',
    label: 'PRO',
    Icon: Sparkles,
  },
  enterprise: {
    bg: 'linear-gradient(135deg, #A57FFF 0%, #6F4EE0 100%)',
    color: '#FFFFFF',
    border: 'rgba(165, 127, 255, 0.55)',
    label: 'ENTERPRISE',
    Icon: Zap,
  },
  beta: {
    bg: 'rgba(0, 212, 255, 0.14)',
    color: '#00D4FF',
    border: 'rgba(0, 212, 255, 0.55)',
    label: 'BETA',
    Icon: Sparkles,
  },
  locked: {
    bg: 'rgba(255, 255, 255, 0.05)',
    color: '#FFFFFF',
    border: 'rgba(255, 255, 255, 0.18)',
    label: 'LOCKED',
    Icon: Lock,
  },
};

export default function ProBadge({
  tier = 'pro',
  size = 'md',
  label,           // override the auto-label if you want something custom
  className = '',
  withIcon = true,
}) {
  const palette = PALETTES[tier] || PALETTES.pro;
  const text = label || palette.label;
  const Icon = palette.Icon;

  const sizeClasses = size === 'sm'
    ? 'px-1.5 py-0.5 text-[8px] tracking-[0.18em] gap-0.5'
    : 'px-2 py-[3px] text-[9.5px] tracking-[0.22em] gap-1';
  const iconSize = size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5';

  return (
    <span
      data-testid={`pro-badge-${tier}`}
      className={`inline-flex items-center font-black uppercase rounded leading-none ${sizeClasses} ${className}`}
      style={{
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        boxShadow: tier === 'pro' ? '0 0 14px rgba(244, 194, 78, 0.35)'
                   : tier === 'enterprise' ? '0 0 14px rgba(165, 127, 255, 0.30)'
                   : 'none',
        verticalAlign: 'middle',
      }}
    >
      {withIcon && <Icon className={iconSize} strokeWidth={2.4} />}
      {text}
    </span>
  );
}
